import { observer } from "mobx-react-lite";
import { useContext } from "react";

import { DefaultButton, Stack, ToolTipButton } from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { ReminderStatuses } from "@modules/clinical/screens/patient-record/components/clinical-reminder/ClinicalReminderManagementFilterBase.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";
import { When } from "@ui-components/withPerm.tsx";

import { InboxScreenContext } from "../../context/InboxScreenContext.ts";

enum ReminderButtonLabels {
  Add = "Add a reminder",
  Send = "Send",
  Contacted = "Contacted",
  Delete = "Delete"
}

export interface RemindersListButtonsProps {
  isFollowUpScreen?: boolean;
}

export const RemindersListButtons: React.FC<RemindersListButtonsProps> =
  observer(({ isFollowUpScreen }) => {
    const {
      setActivityDialogVisible,
      activityDialogVisible,
      selectedClinicalReminderRows,
      sendSelectedReminders,
      manuallyProcessSelectedReminders,
      setReminderSendWarningVisible
    } = useContext(InboxScreenContext);

    const { clinical } = useStores();

    const hasPoisonedSelected = selectedClinicalReminderRows.find(
      x => !x.patient?.hasClinicalReminderPreference
    );

    const singleNonconsentSelection =
      hasPoisonedSelected && selectedClinicalReminderRows.length === 1;

    const patientLabel = usePatientLabel();

    const actionableStatuses: string[] = [
      ReminderStatuses.Contacted,
      ReminderStatuses.Delivered,
      ReminderStatuses.Sent
    ];

    const containsAppointmentsWithinRange =
      clinical.clinicalActivitiesPreference?.useReminderMinRange &&
      selectedClinicalReminderRows.some(x => x.withinAppointmentRange);

    const selectedContainsNonActionable =
      selectedClinicalReminderRows.some(
        x =>
          x.clinicalReminderComm?.reminderStatus &&
          isFollowUpScreen &&
          !actionableStatuses.includes(x.clinicalReminderComm?.reminderStatus)
      ) || containsAppointmentsWithinRange;

    return (
      <>
        {!isFollowUpScreen && (
          <When
            permission={[
              Permission.ClinActivityWrite,
              Permission.ClinicalReminderWrite,
              Permission.ClinicalReminderCreate
            ]}
          >
            <Stack.Item>
              <DefaultButton
                disabled={clinical.ui.clinicalActivityActionDisabled}
                onClick={() =>
                  setActivityDialogVisible({
                    ...activityDialogVisible,
                    add: true
                  })
                }
                text={ReminderButtonLabels.Add}
                iconProps={{ iconName: "Add" }}
              />
            </Stack.Item>
          </When>
        )}

        <When
          permission={[
            Permission.ClinicalReminderSend,
            Permission.ClinicalReminderWrite
          ]}
        >
          <ToolTipButton
            buttonProps={{
              disabled:
                selectedClinicalReminderRows.length === 0 ||
                selectedContainsNonActionable,
              text: singleNonconsentSelection
                ? ReminderButtonLabels.Contacted
                : ReminderButtonLabels.Send,
              onClick: () => {
                if (singleNonconsentSelection) {
                  manuallyProcessSelectedReminders();
                  return;
                }

                if (!hasPoisonedSelected) {
                  sendSelectedReminders();
                } else {
                  setReminderSendWarningVisible(true);
                }
              },
              iconProps: {
                iconName: singleNonconsentSelection ? "IncomingCall" : "Send"
              },
              primary: true
            }}
            toolTipContent={
              hasPoisonedSelected && !singleNonconsentSelection
                ? `Some selected ${patientLabel}(s) have no mobile number recorded`
                : undefined
            }
          />
        </When>

        {!isFollowUpScreen && (
          <When permission={[Permission.ClinicalReminderDelete]}>
            <Stack.Item>
              <DefaultButton
                disabled={
                  clinical.ui.clinicalActivityActionDisabled ||
                  selectedClinicalReminderRows.length === 0
                }
                onClick={() =>
                  setActivityDialogVisible({
                    ...activityDialogVisible,
                    delete: true
                  })
                }
                text={ReminderButtonLabels.Delete}
              />
            </Stack.Item>
          </When>
        )}
      </>
    );
  });
